import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import { useNavigate, useLocation } from "react-router";
import {
  EquipmentGroup,
  updateEquipment,
} from "../../services/equipmentService";
import { uploadFile } from "../../services/exerciseService";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import { getEquipmentGroups } from "../../services/equipmentService";
import UploadButton from "../../components/UploadButton";

const EquipmentValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Equipment name is required")
    .matches(/^(?!\s*$).+/, "Equipment name cannot be empty or just spaces"),
  group: Yup.string().required("Equipment group is required"),
  imageUrl: Yup.string().required("Image is required"),
});

interface EquipmentFormValues {
  name: string;
  group?: string;
  imageUrl?: string | null;
}

const EditEquipment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location || {};
  const { equipment } = state || {};

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [equipmentGroups, setEquipmentGroups] = useState<EquipmentGroup[]>([]);
  const [imagePreview] = useState<string | null>(equipment?.imageUrl || null);

  useEffect(() => {
    const fetchEquipmentGroups = async () => {
      try {
        const groups = await getEquipmentGroups();
        setEquipmentGroups(groups);
      } catch (error) {
        console.error("Error fetching equipment groups:", error);
      }
    };

    fetchEquipmentGroups();
  }, []);

  const initialValues: EquipmentFormValues = {
    name: equipment?.name || "",
    group: equipment?.group?.id?.toString() || "",
    imageUrl: equipment?.imageUrl || null,
  };

  const handleImageUpload = async (
    file: File | null,
    setFieldValue: (field: string, value: any) => void
  ): Promise<string | null> => {
    if (!file) {
      return null;
    }

    try {
      const uploaded = await uploadFile(file);

      if (uploaded?.url) {
        setFieldValue("imageUrl", uploaded.url);
        return uploaded.url;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      return null;
    }
  };

  const handleRemoveImage = (
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("imageUrl", null);
  };

  const handleSubmit = async (values: EquipmentFormValues) => {
    console.log(values);

    try {
      const selectedGroup = equipmentGroups.find(
        (g) => g.id.toString() === values.group
      );
      const equipmentData = {
        name: values.name,
        targetMuscles: equipment.targetMuscles || [],
        availableWeights: equipment.availableWeights || [],
        imageUrl: values.imageUrl || undefined,
        group: selectedGroup || undefined,
      };
      await updateEquipment(equipment.id, equipmentData);
      setSuccessMessage("Equipment updated successfully!");
      setTimeout(() => {
        navigate("/equipments");
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update equipment"
      );
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <Header title="Edit Equipment" />
      <div className="w-full max-w-lg bg-white shadow-md rounded-md p-6">
        {error && (
          <ErrorMessage message={error} onClose={() => setError(null)} />
        )}

        {successMessage && (
          <SuccessMessage
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
        )}

        <div className="flex items-center justify-between mt-4 border-b pb-4">
          <BackButton onClick={() => navigate("/equipments")} />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={EquipmentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, dirty }) => {
            console.log(errors);
            return (
              <Form>
                <div className="mb-4">
                  <label
                    htmlFor="name"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Equipment Name
                  </label>
                  <Field
                    id="name"
                    name="name"
                    maxLength={30}
                    type="text"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                    placeholder="Enter equipment item"
                  />
                  {errors.name && touched.name && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.name}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="group"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Equipment Group
                  </label>
                  <Field
                    as="select"
                    id="group"
                    name="group"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                  >
                    <option value="">Select group</option>
                    {equipmentGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Field>
                  {errors.group && touched.group && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.group}
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Upload Image
                  </label>
                  <UploadButton
                    label="Upload image"
                    onUpload={(file) => handleImageUpload(file, setFieldValue)}
                    onRemove={() => handleRemoveImage(setFieldValue)}
                    accept="image/*"
                    index={0}
                  />
                  {errors.imageUrl && touched.imageUrl && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.imageUrl}
                    </div>
                  )}
                  <div className="mt-4">
                    <label className="block text-gray-700 font-medium mb-2">
                      Current Image
                    </label>
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-32 object-cover mt-4 rounded"
                      />
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className={`w-full py-2 text-white font-semibold rounded-md transition ${
                    dirty
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                  disabled={!dirty}
                >
                  Save Changes
                </button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default EditEquipment;
